import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from '@qr-front/ui/components/Button'
import { cn } from '@qr-front/ui/utils'
import { RhcField } from 'features/generator/components/RhcField'
import { useTranslations } from 'next-intl'
import { Resolver, useForm } from 'react-hook-form'
import { AnyObjectSchema } from 'yup'

type EditableFieldForm = {
	value: string
}

type EditableFieldProps = {
	value: string
	onSave: (newValue: string) => void
	onCancel: () => void
	label?: string
	placeholder?: string
	type?: string
	className?: string
	validationSchema?: AnyObjectSchema
	rendersOn?: 'mobile' | 'desktop'
	serverError?: string
}

export function EditableField({
	value,
	onSave,
	onCancel,
	label,
	placeholder,
	type = 'text',
	className,
	validationSchema,
	rendersOn,
	serverError,
}: EditableFieldProps) {
	const t = useTranslations()

	const form = useForm<EditableFieldForm>({
		defaultValues: { value },
		mode: 'onChange',
		resolver:
			validationSchema &&
			(yupResolver(
				validationSchema,
			) as unknown as Resolver<EditableFieldForm>),
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	const handleSave = (data: EditableFieldForm) => {
		onSave(data.value)
	}

	const handleCancel = () => {
		form.reset()
		onCancel()
	}

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault()
			form.handleSubmit(handleSave)()
		}
	}

	const rendersOnDesktop = rendersOn === 'desktop'
	const desktopButtonCls = 'h-[30px] rounded-4 px-2 py-1'
	const mobileTabletButtonCls = 'w-1/2'

	const buttonCls = cn(
		rendersOnDesktop ? desktopButtonCls : mobileTabletButtonCls,
	)

	return (
		<>
			<form
				onSubmit={form.handleSubmit(handleSave)}
				className={cn(
					'flex',
					rendersOnDesktop ? 'gap-2' : 'w-full flex-col gap-6',
					className,
				)}
				noValidate
			>
				<RhcField
					type={type}
					form={form}
					path="value"
					value={value}
					required
					setValue={(val) => form.setValue('value', val || '')}
					inputClassName={cn(
						rendersOnDesktop &&
							'type-body-small h-[30px] w-[220px] rounded-4 px-2 py-1',
					)}
					label={label}
					placeholder={placeholder}
					deselectAndFocus
					error={serverError || form.formState.errors.value?.message}
					onKeyDown={handleKeyDown}
				/>
				<div
					className={cn(
						'flex gap-4',
						rendersOnDesktop && 'flex w-full gap-2',
					)}
				>
					<Button
						visual="secondary"
						onClick={handleCancel}
						className={buttonCls}
					>
						{t('ui__generic__cancel')}
					</Button>
					<Button type="submit" className={buttonCls}>
						{t('ui__generic__save')}
					</Button>
				</div>
			</form>
		</>
	)
}
